/*
 * Testimonials Carousel — Swiper-backed module.
 *
 * Builds two synced Swiper instances (image track + content track)
 * per [data-testimonials] block on the page (forEach pattern so
 * multiple carousels each get their own state). Re-runs on
 * shopify:section:load when the customizer reloads a section.
 *
 * Liquid output is intentionally flat — content_for renders each
 * _testimonial-item's snippet as two siblings inside .testimonials__track
 * (image-slide + text-slide). This JS rearranges them into Swiper's
 * expected DOM (.swiper > .swiper-wrapper > .swiper-slide) at init,
 * then initialises the two Swipers linked via controller.control so
 * navigating one drives the other.
 *
 * Image swiper uses effect: 'fade' so images always cross-fade.
 * Content swiper uses effect: 'slide' (default) or 'fade' per the
 * block's data-animation attribute.
 *
 * Swiper is loaded as a UMD bundle (vendored to
 * assets/swiper-bundle.min.js) by a regular <script> tag in
 * snippets/scripts.liquid earlier in the document, exposing a global
 * `Swiper` constructor. We don't use ESM imports because the swiper
 * ESM bundle relies on neighbouring relative-path files that aren't
 * vendored — the UMD bundle is fully self-contained.
 */

const Swiper = window.Swiper;

const SELECTOR = '[data-testimonials]';
const INSTANCE_KEY = '_testimonialsInstance';

class TestimonialsCarousel {
  constructor(root) {
    this.root = root;
    this.autoplayEnabled = root.dataset.autoplay === 'true';
    this.delay = (parseFloat(root.dataset.delay) || 5) * 1000;
    this.paginationEnabled = root.dataset.pagination === 'true';
    this.animation = root.dataset.animation === 'fade' ? 'fade' : 'slide';
    this.layout = root.dataset.layout || 'image_with_text';
    this.abortController = new AbortController();
    this.imageSwiper = null;
    this.contentSwiper = null;

    this.init();
  }

  init() {
    const track = this.root.querySelector('.testimonials__track');
    if (!track) return;

    const imageSlides = Array.from(track.querySelectorAll(':scope > .testimonials__image-slide'));
    const textSlides = Array.from(track.querySelectorAll(':scope > .testimonials__text-slide'));
    if (textSlides.length === 0) return;

    // Rearrange the flat content_for output into Swiper's expected
    // .swiper > .swiper-wrapper > .swiper-slide structure. Slides keep
    // their existing classes; Swiper looks up children by .swiper-slide
    // (the snippet adds that class server-side).
    const hasImages = this.layout === 'image_with_text' && imageSlides.length > 0;

    let imageContainer = null;
    if (hasImages) {
      imageContainer = this._buildSwiperContainer('testimonials__images', imageSlides);
    }
    const contentContainer = this._buildSwiperContainer('testimonials__content', textSlides);

    // Pagination element lives inside the content swiper so its dots
    // sit visually under the text column. Swiper positions it via its
    // own CSS (we override the bullet classes below).
    let paginationEl = null;
    if (this.paginationEnabled && textSlides.length > 1) {
      paginationEl = document.createElement('div');
      paginationEl.className = 'testimonials__pagination swiper-pagination';
      contentContainer.appendChild(paginationEl);
    }

    // Swap the track's children for the new Swiper containers.
    track.replaceChildren();
    if (imageContainer) track.appendChild(imageContainer);
    track.appendChild(contentContainer);

    // Build Swiper instances.
    const sharedOptions = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: textSlides.length > 1,
      autoplay: this.autoplayEnabled
        ? { delay: this.delay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
    };

    if (imageContainer) {
      this.imageSwiper = new Swiper(imageContainer, {
        ...sharedOptions,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        // Image carousel has no pagination of its own; the content
        // swiper's pagination is the shared control.
      });
    }

    this.contentSwiper = new Swiper(contentContainer, {
      ...sharedOptions,
      effect: this.animation === 'fade' ? 'fade' : 'slide',
      fadeEffect: { crossFade: true },
      // autoHeight sizes the swiper-wrapper to the active slide's
      // content instead of filling the column. That keeps the
      // pagination (which sits after the wrapper in flow) hugging
      // the bottom of the text content rather than getting pinned
      // to the column's bottom edge.
      autoHeight: true,
      pagination: paginationEl
        ? {
            el: paginationEl,
            clickable: true,
            bulletClass: 'testimonials__dot',
            bulletActiveClass: 'testimonials__dot--active',
          }
        : false,
    });

    // Sync the two swipers via Swiper's controller module so dragging
    // the content track moves the images, and vice versa.
    if (this.imageSwiper) {
      this.imageSwiper.controller.control = this.contentSwiper;
      this.contentSwiper.controller.control = this.imageSwiper;
    }

    this.root.classList.add('is-initialized');

    this._bindCustomizerHooks(textSlides);
  }

  _buildSwiperContainer(className, slideElements) {
    const container = document.createElement('div');
    container.className = `${className} swiper`;
    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';
    slideElements.forEach((slide) => {
      slide.classList.add('swiper-slide');
      wrapper.appendChild(slide);
    });
    container.appendChild(wrapper);
    return container;
  }

  _bindCustomizerHooks(textSlides) {
    // When the merchant clicks a testimonial item in the block tree,
    // jump the carousel to that slide so they can see what they're
    // editing. shopify_attributes is on the text-slide (see the
    // testimonial-slide snippet), so the event target is inside the
    // matching text-slide.
    document.addEventListener(
      'shopify:block:select',
      (e) => {
        if (!this.root.contains(e.target)) return;
        const idx = textSlides.findIndex(
          (slide) => slide === e.target || slide.contains(e.target)
        );
        if (idx >= 0 && this.contentSwiper) {
          // slideToLoop respects the loop config; otherwise use slideTo.
          if (this.contentSwiper.params.loop) {
            this.contentSwiper.slideToLoop(idx);
          } else {
            this.contentSwiper.slideTo(idx);
          }
        }
      },
      { signal: this.abortController.signal }
    );
  }

  destroy() {
    this.abortController.abort();
    if (this.imageSwiper) {
      this.imageSwiper.destroy(true, true);
      this.imageSwiper = null;
    }
    if (this.contentSwiper) {
      this.contentSwiper.destroy(true, true);
      this.contentSwiper = null;
    }
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new TestimonialsCarousel(el);
  });
}

function destroyAll(root) {
  const scope = root || document;
  scope.querySelectorAll(SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) {
      el[INSTANCE_KEY].destroy();
      el[INSTANCE_KEY] = null;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => destroyAll(e.target));
